/**
 * Créditos — v1.12 (Feb 2026).
 *
 * Rebuilt from scratch to fully match reference image 4 / image 5.
 *
 * Sections (top to bottom):
 *   1. Header:  ← back · title "Créditos" · Créditos:XX pill · avatar
 *   2. Balance card: holographic N-coin with orbital ring · CRÉDITOS
 *      DISPONIBLES + big number + "créditos" + Plan + renewal · [+]
 *      Añadir créditos circular CTA
 *   3. Package grid 3×2 (100 · 250 MÁS POPULAR · 500 // 1250 · 2500 ·
 *      5000). Featured card gets the cyan glow ring + [+ MÁS POPULAR]
 *      pill badge on top. Each card has small coin icon, big number,
 *      "créditos" caption, price, "Comprar →" pill.
 *   4. Trust strip: "Pagos 100% seguros con Stripe" + three sub-chips
 *      (Pago seguro · Proceso rápido · Protección de datos).
 *   5. "Otra cantidad" row (icon + copy + "Elige cantidad →")
 *   6. "Historial de recargas" row (icon + copy + chevron)
 *   7. Footer note: "Tus créditos se activan al instante después del pago."
 *
 * Purchase & Success flows are handled by <PurchaseModal /> and
 * <SuccessModal /> — CENTERED overlays, dark backdrop, premium feel.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, CheckCircle2, ChevronRight, Clock, Coins, History,
  Loader2, Lock, Plus, Shield, ShieldCheck, Sliders, Sparkles, X, Zap,
} from 'lucide-react';
import { apiService } from '../services/api';
import { useCredits } from '../contexts/CreditsContext';
import { useAuth } from '../contexts/AuthContext';
import { useLocale, useT } from '../contexts/LocaleContext';
import { toast } from 'sonner';
import { NexusCoin as HolographicNexusCoin } from '../components/nx/NexusCoin';
import { AdminAvatarMenu } from '../components/shell/AdminAvatarMenu';

type Pkg = { id: string; credits: number; price_usd: number; featured?: boolean };

const FALLBACK_PACKAGES: Pkg[] = [
  { id: 'credits_100',  credits: 100,  price_usd: 20 },
  { id: 'credits_250',  credits: 250,  price_usd: 50, featured: true },
  { id: 'credits_500',  credits: 500,  price_usd: 100 },
  { id: 'credits_1250', credits: 1250, price_usd: 250 },
  { id: 'credits_2500', credits: 2500, price_usd: 500 },
  { id: 'credits_5000', credits: 5000, price_usd: 1000 },
];

const PKG_DESCRIPTIONS: Record<string, string> = {
  credits_100:  'Ideal para probar y ajustes rápidos',
  credits_250:  'Perfecto para proyectos pequeños',
  credits_500:  'Ideal para proyectos pequeños y medianos',
  credits_1250: 'Excelente para trabajo continuo',
  credits_2500: 'Óptimo para equipos y agencias',
  credits_5000: 'Máximo aprovechamiento',
};

export default function Creditos() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { balance, refresh } = useCredits();
  const t = useT();
  const { locale } = useLocale();
  const [pkgs, setPkgs] = useState<Pkg[]>(FALLBACK_PACKAGES);
  const [selected, setSelected] = useState<Pkg | null>(null);
  const [success, setSuccess] = useState<Pkg | null>(null);

  const displayBalance = balance ?? 0;
  const isPro = user?.role === 'admin' || user?.role === 'owner';
  const planLabel = isPro ? `Plan ${t('credits.planPro')}` : `Plan ${t('credits.planFree')}`;
  const nextRenewal = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toLocaleDateString(locale === 'en' ? 'en-US' : 'es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  }, [locale]);

  const load = useCallback(async () => {
    try {
      const info = await (apiService as any).getBillingInfo?.();
      if (info?.packages?.length) {
        const mapped = info.packages
          .filter((p: any) => !p.is_welcome)
          .map((p: any) => ({
            id: p.id, credits: p.credits,
            price_usd: Number(p.price_usd || p.amount_usd || 0),
            featured: p.id === 'credits_250',
          }));
        if (mapped.length) setPkgs(mapped);
      }
    } catch { /* keep fallback */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Handle Stripe return: ?paid={session_id}
  // v1.15 · Poll con backoff hasta que el webhook confirme el crédito.
  // Sprint P0 Lanzamiento · Poll extendido hasta 60 s + refresh del
  // balance en CADA tick (no solo al final). Root cause reportado por
  // el usuario: el saldo no aparecía tras el pago aunque Stripe
  // hubiera confirmado. Ahora aseguramos que el estado del user se
  // actualice repetidamente hasta ver los créditos.
  useEffect(() => {
    const sid = params.get('paid');
    if (!sid) return;
    let cancelled = false;
    let toastId: string | number | undefined;
    (async () => {
      toastId = toast.loading(t('credits.purchaseBusy'));
      // ~60 s total: 0,1,2,3,4,5,5,5,5,5,5,5,5,5,5 = 63 s max
      const delays = [0, 1000, 2000, 3000, 4000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000];
      let completed = false;
      for (const d of delays) {
        if (cancelled) return;
        if (d) await new Promise(r => setTimeout(r, d));
        try {
          const s = await apiService.getCheckoutStatus(sid);
          if (s.state === 'completed') {
            toast.dismiss(toastId);
            setSuccess({ id: s.package_id, credits: s.credits, price_usd: s.amount_cents / 100 });
            // Force refresh AT LEAST twice to defeat any stale cache.
            await refresh?.({ force: true });
            await new Promise(r => setTimeout(r, 500));
            await refresh?.({ force: true });
            completed = true;
            break;
          }
          if (s.state === 'failed') {
            toast.dismiss(toastId);
            toast.error(t('credits.purchaseFail'));
            break;
          }
          // Still pending — proactively refresh the balance anyway
          // (in case the webhook already fired but the UI cache is stale).
          try { await refresh?.({ force: true }); } catch { /* noop */ }
        } catch { /* keep polling — webhook may still be flying */ }
      }
      if (!completed && !cancelled) {
        const final = await apiService.getCheckoutStatus(sid).catch(() => null);
        if (final && final.state !== 'completed' && final.state !== 'failed') {
          toast.dismiss(toastId);
          toast.info('Tu pago está en proceso. El saldo aparecerá en unos segundos.');
        }
      }
      params.delete('paid');
      setParams(params, { replace: true });
    })();
    return () => { cancelled = true; if (toastId) toast.dismiss(toastId); };
  }, [params, refresh, setParams]);

  const featured = useMemo(() => pkgs.find(p => p.featured) || pkgs[1], [pkgs]);

  const startCheckout = async () => {
    if (!selected) return;
    // Sprint Corrección Final: los administradores NO deben pasar por
    // Stripe. Se acreditan créditos directamente vía el endpoint interno
    // `/api/admin/users/{id}/grant`, con audit log inmutable.
    if (user?.role === 'admin' && user?.id) {
      try {
        await (apiService as any).grantCreditsToUser?.(
          user.id,
          selected.credits,
          `admin self-grant · ${selected.id}`,
        );
        toast.success(`+${selected.credits} créditos acreditados (modo administrador).`);
        await refresh?.({ force: true });
        setSuccess(selected);
        setSelected(null);
        return;
      } catch (err: any) {
        toast.error(err?.response?.data?.detail || 'No se pudo acreditar créditos.');
        return;
      }
    }
    // Usuario normal → Stripe Checkout OBLIGATORIO. Nunca acreditar
    // créditos antes de la confirmación oficial del webhook.
    // Sprint Corrección Crítica: garantizamos que la navegación al
    // Checkout de Stripe se ejecute sin interceptores.
    try {
      const r = await apiService.createCheckoutSession(selected.id, { returnTo: '/creditos' });
      // Debug info para producción — expuesto en la consola del navegador
      // para que el usuario/admin pueda diagnosticar rápidamente.
      // eslint-disable-next-line no-console
      console.info('[Nexus/Stripe] checkout response', {
        mock: r?.mock, live_mode: r?.live_mode,
        has_url: !!r?.checkout_url,
        url_host: r?.checkout_url ? new URL(r.checkout_url).host : null,
      });
      // Bloqueo defensivo: SOLO si el backend nos dice explícitamente
      // que está en modo mock, no acreditamos ni redirigimos.
      if (r?.mock === true) {
        toast.error('Stripe está en modo de prueba en este entorno. Contacta con soporte.');
        return;
      }
      // Validación de URL: confiamos en cualquier URL de Stripe
      // (checkout.stripe.com sirve tanto para test como para live).
      const url = r?.checkout_url || '';
      const isStripeUrl = /^https:\/\/(checkout\.stripe\.com|billing\.stripe\.com)\//.test(url);
      if (!isStripeUrl) {
        toast.error('No se recibió una URL válida de Stripe. Contacta con soporte.');
        return;
      }
      // Cerrar el modal ANTES de redirigir para evitar cualquier
      // ambigüedad visual y pantalla de éxito prematura.
      setSelected(null);
      toast.loading('Abriendo Stripe Checkout…', { id: 'stripe-redirect' });
      // window.location.assign es la forma más explícita de forzar
      // navegación completa (equivalente a hacer clic en un enlace).
      window.location.assign(url);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'No se pudo iniciar el pago.');
    }
  };

  // Sprint Corrección Final · Compra con monto personalizado.
  // Cualquier importe válido se convierte a créditos usando el mismo
  // credit_value_usd que muestra el motor financiero.
  const [customUsd, setCustomUsd] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const creditValueUsd = 0.20;  // debe coincidir con backend default
  const customCredits = Math.max(0, Math.round(Number(customUsd || 0) / creditValueUsd));
  const submitCustom = async () => {
    const amt = Number(customUsd);
    if (!Number.isFinite(amt) || amt < 1) {
      toast.error('Ingresa un importe mínimo de $1 USD.');
      return;
    }
    if (user?.role === 'admin' && user?.id) {
      try {
        await (apiService as any).grantCreditsToUser?.(
          user.id,
          customCredits,
          `admin custom-grant · $${amt}`,
        );
        toast.success(`+${customCredits} créditos acreditados.`);
        await refresh?.({ force: true });
        setShowCustom(false);
        setCustomUsd('');
      } catch (err: any) {
        toast.error(err?.response?.data?.detail || 'No se pudo acreditar créditos.');
      }
      return;
    }
    // Usuario normal → Stripe OBLIGATORIO.
    try {
      const r = await (apiService as any).createCustomCheckout?.(amt, { returnTo: '/creditos' });
      // eslint-disable-next-line no-console
      console.info('[Nexus/Stripe] custom checkout response', {
        mock: r?.mock, live_mode: r?.live_mode,
        has_url: !!r?.checkout_url,
        url_host: r?.checkout_url ? new URL(r.checkout_url).host : null,
      });
      if (r?.mock === true) {
        toast.error('Stripe está en modo de prueba en este entorno. Contacta con soporte.');
        return;
      }
      const url = r?.checkout_url || '';
      const isStripeUrl = /^https:\/\/(checkout\.stripe\.com|billing\.stripe\.com)\//.test(url);
      if (!isStripeUrl) {
        toast.error('No se recibió una URL válida de Stripe. Contacta con soporte.');
        return;
      }
      setShowCustom(false);
      toast.loading('Abriendo Stripe Checkout…', { id: 'stripe-redirect' });
      window.location.assign(url);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'No se pudo iniciar el pago.');
    }
  };

  // Sprint Corrección Crítica · Historial de recargas modal.
  const [showHistory, setShowHistory] = useState(false);
  const [historyItems, setHistoryItems] = useState<any[] | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const openHistory = async () => {
    setShowHistory(true);
    if (historyItems !== null) return;
    setLoadingHistory(true);
    try {
      const rows = await apiService.getBillingLedger(100);
      // Solo mostramos recargas positivas (deltas > 0): compras Stripe,
      // welcome gifts y grants de admin. Excluimos consumos LLM (delta < 0).
      const filtered = (rows || []).filter((r: any) => Number(r.delta) > 0);
      setHistoryItems(filtered);
    } catch {
      setHistoryItems([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  return (
    <div className="min-h-screen text-slate-100" data-testid="creditos-page"
         style={{ background: 'hsl(var(--bg))' }}>
      {/* Header */}
      <header className="sticky top-0 z-20 backdrop-blur-md"
        style={{
          background: 'linear-gradient(180deg, hsl(var(--bg-elev) / 0.9), hsl(var(--bg-elev) / 0.5))',
          borderBottom: '1px solid hsl(var(--border))',
        }}>
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => nav('/home')} className="w-9 h-9 rounded-lg grid place-items-center shrink-0"
            style={{ background: 'hsl(var(--bg-elev))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text-muted))' }}
            data-testid="creditos-back" aria-label={t('credits.back')}>
            <ArrowLeft size={16} />
          </button>
          <div className="flex-1 text-center">
            <div className="text-xs tracking-[0.28em] font-medium uppercase" style={{ color: 'hsl(var(--text))' }}>{t('credits.pageTitle')}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
              style={{
                background: displayBalance < 0 ? 'hsl(0 84% 65% / 0.12)' : 'hsl(var(--accent) / 0.12)',
                border: `1px solid ${displayBalance < 0 ? 'hsl(0 84% 65% / 0.4)' : 'hsl(var(--accent) / 0.35)'}`,
                color: displayBalance < 0 ? 'hsl(0 84% 65%)' : 'hsl(var(--accent))',
              }}>
              <Coins size={12} /> {t('credits.label')} {displayBalance}
            </div>
            <div data-testid="creditos-avatar-wrap"><AdminAvatarMenu /></div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-3 space-y-3">
        {/* 1 · Balance card with holographic N-coin */}
        <section
          className="nx-glass nx-halo-medium relative overflow-hidden p-5 sm:p-6 flex items-center gap-5"
          data-testid="credits-balance-card"
        >
          <HolographicNexusCoin size={150} testId="credits-nexus-coin" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: '#00F0FF' }}>
              {t('credits.availableCredits')}
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <div
                className="font-semibold tabular-nums leading-none"
                style={{
                  color: displayBalance < 0 ? 'hsl(0 84% 65%)' : '#FFFFFF',
                  fontSize: 'clamp(2.75rem, 8vw, 4rem)',
                }}
                data-testid="credits-balance-number"
              >
                {displayBalance}
              </div>
              <div className="text-sm" style={{ color: '#9BA1A6' }}>{t('credits.creditsWord')}</div>
            </div>
            <div className="mt-2 text-[11px] flex items-center gap-1.5" style={{ color: '#9BA1A6' }}>
              <span style={{ color: '#00F0FF' }}>{planLabel}</span>
              <span>·</span>
                <span>{t('credits.renewal')}: {nextRenewal}</span>
            </div>
          </div>
          <button
            onClick={() => setSelected(featured)}
            className="hidden sm:flex flex-col items-center gap-1 shrink-0"
            data-testid="credits-add-btn"
            aria-label={t('credits.addCreditsCta')}
          >
            <span className="w-14 h-14 rounded-full grid place-items-center transition"
              style={{
                background: 'linear-gradient(160deg, #00F0FF, #33F3FF)',
                color: '#04121a',
                boxShadow:
                  '0 0 0 1px rgba(0, 240, 255, 0.55), ' +
                  '0 0 22px rgba(0, 240, 255, 0.5), ' +
                  '0 12px 28px -8px rgba(0, 240, 255, 0.55)',
              }}>
              <Plus size={22} strokeWidth={2.5} />
            </span>
            <span className="text-[11px] font-medium mt-1 text-center leading-tight" style={{ color: '#00F0FF' }}>{t('credits.addCreditsCta')}</span>
          </button>
        </section>

        {/* 2 · Package grid */}
        <section>
          <div className="mb-3">
            <div className="text-[17px] font-semibold" style={{ color: 'hsl(var(--text))' }}>{t('credits.pickPackage')}</div>
            <div className="text-xs mt-0.5" style={{ color: 'hsl(var(--text-muted))' }}>{t('credits.pickPackageSub')}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {pkgs.map(pkg => (
              <PackageCard key={pkg.id} pkg={pkg} onSelect={() => setSelected(pkg)} />
            ))}
          </div>
        </section>

        {/* 3 · Trust strip · Pagos 100% seguros con Stripe */}
        <section
          className="rounded-2xl p-4"
          style={{ background: 'hsl(var(--bg-elev) / 0.55)', border: '1px solid hsl(var(--border))' }}
          data-testid="credits-trust-card"
        >
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0"
              style={{ background: 'hsl(var(--accent) / 0.15)', border: '1px solid hsl(var(--accent) / 0.35)', color: 'hsl(var(--accent))' }}>
              <ShieldCheck size={16} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">{t('credits.trustStripTitle')}</div>
              <div className="text-[11px]" style={{ color: 'hsl(var(--text-muted))' }}>{t('credits.trustStripSub')}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 pt-3" style={{ borderTop: '1px dashed hsl(var(--border))' }}>
            {[
              { icon: <Lock size={11} />, label: t('credits.trustSecure') },
              { icon: <Zap size={11} />, label: t('credits.trustFast') },
              { icon: <Shield size={11} />, label: t('credits.trustPrivacy') },
            ].map(f => (
              <div key={f.label} className="text-center">
                <div className="inline-flex items-center gap-1 text-[10px]" style={{ color: 'hsl(var(--text-muted))' }}>
                  <span style={{ color: 'hsl(var(--accent))' }}>{f.icon}</span> {f.label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 4 · Otra cantidad */}
        <button
          onClick={() => setShowCustom(true)}
          className="w-full text-left rounded-2xl p-4 flex items-center gap-3 transition"
          style={{ background: 'hsl(var(--bg-elev) / 0.55)', border: '1px solid hsl(var(--border))' }}
          data-testid="credits-otra-cantidad"
        >
          <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0"
            style={{ background: 'hsl(var(--accent) / 0.12)', border: '1px solid hsl(var(--accent) / 0.3)', color: 'hsl(var(--accent))' }}>
            <Sliders size={16} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">{t('credits.customAmountTitle')}</div>
            <div className="text-[11px]" style={{ color: 'hsl(var(--text-muted))' }}>{t('credits.customAmountBody')}</div>
          </div>
          <div className="text-xs inline-flex items-center gap-1" style={{ color: 'hsl(var(--accent))' }}>
            {t('credits.customAmountCta')} <ArrowRight size={12} />
          </div>
        </button>

        {/* 5 · Historial de recargas */}
        <button
          onClick={openHistory}
          className="w-full text-left rounded-2xl p-4 flex items-center gap-3 transition"
          style={{ background: 'hsl(var(--bg-elev) / 0.55)', border: '1px solid hsl(var(--border))' }}
          data-testid="credits-history-btn"
        >
          <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0"
            style={{ background: 'hsl(var(--accent) / 0.12)', border: '1px solid hsl(var(--accent) / 0.3)', color: 'hsl(var(--accent))' }}>
            <History size={16} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">{t('credits.historyTitle')}</div>
            <div className="text-[11px]" style={{ color: 'hsl(var(--text-muted))' }}>{t('credits.historyBody')}</div>
          </div>
          <ChevronRight size={14} style={{ color: 'hsl(var(--text-muted))' }} />
        </button>

        <div className="text-center text-[11px] pt-1" style={{ color: 'hsl(var(--text-muted))' }}>
          <Clock size={11} className="inline mr-1 -mt-0.5" /> {t('credits.footerNote')}
        </div>
      </main>

      {selected && (
        <PurchaseModal pkg={selected} onClose={() => setSelected(null)} onConfirm={startCheckout} />
      )}
      {success && (
        <SuccessModal pkg={success} onClose={() => setSuccess(null)}
          onContinue={() => { setSuccess(null); nav('/home'); }} />
      )}
      {/* Sprint Corrección Crítica · Otra cantidad → Stripe / Admin grant.
          Modal responsive optimizado para iPhone/Android. */}
      {showCustom && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
          onClick={() => setShowCustom(false)}
          data-testid="credits-custom-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:w-[440px] sm:max-w-full rounded-t-3xl sm:rounded-2xl p-5 sm:p-6 border max-h-[92dvh] overflow-y-auto"
            style={{
              background: 'hsl(var(--bg-elev))',
              borderColor: 'hsl(var(--accent) / 0.35)',
              paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))',
            }}
          >
            {/* Drag handle solo en móvil */}
            <div className="sm:hidden mx-auto w-10 h-1 rounded-full mb-3"
                 style={{ background: 'hsl(var(--border))' }} />
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold mb-1"
                     style={{ color: 'hsl(var(--accent))' }}>
                  {t('credits.buyCredits')}
                </div>
                <div className="text-lg font-medium" style={{ color: 'hsl(var(--text))' }}>
                  {t('credits.chooseAmount')}
                </div>
              </div>
              <button
                onClick={() => setShowCustom(false)}
                className="w-8 h-8 rounded-full grid place-items-center shrink-0"
                style={{ background: 'hsl(var(--bg))', color: 'hsl(var(--text-muted))', border: '1px solid hsl(var(--border))' }}
                aria-label="Cerrar"
                data-testid="credits-custom-close"
              >
                <X size={14} />
              </button>
            </div>
            <label className="text-xs" style={{ color: 'hsl(var(--text-muted))' }}>Importe en USD</label>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-lg" style={{ color: 'hsl(var(--accent))' }}>$</span>
              <input
                type="number" min={1} step="1"
                inputMode="decimal"
                value={customUsd}
                onChange={(e) => setCustomUsd(e.target.value)}
                autoFocus
                placeholder="25"
                className="flex-1 min-w-0 px-3 py-2.5 rounded-lg outline-none text-lg font-semibold"
                style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }}
                data-testid="credits-custom-amount"
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {[5, 15, 30, 50, 125, 300].map((v) => (
                <button key={v}
                  onClick={() => setCustomUsd(String(v))}
                  data-testid={`credits-custom-preset-${v}`}
                  className="px-3 py-1.5 rounded-full text-xs font-medium"
                  style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text-muted))' }}
                >
                  ${v}
                </button>
              ))}
            </div>
            <div className="mt-5 rounded-xl p-3"
                 style={{ background: 'hsl(var(--accent) / 0.08)', border: '1px solid hsl(var(--accent) / 0.25)' }}>
              <div className="flex items-center justify-between text-sm gap-2">
                <span className="min-w-0" style={{ color: 'hsl(var(--text-muted))' }}>Créditos que recibes</span>
                <span className="tabular-nums font-semibold shrink-0" style={{ color: 'hsl(var(--accent))' }} data-testid="credits-custom-credits">
                  {customCredits.toLocaleString('es-ES')}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs mt-1 gap-2">
                <span style={{ color: 'hsl(var(--text-muted))' }}>Total</span>
                <span className="tabular-nums shrink-0" style={{ color: 'hsl(var(--text))' }}>
                  ${Number(customUsd || 0).toFixed(2)} USD
                </span>
              </div>
            </div>
            <div className="mt-5 flex flex-col-reverse sm:flex-row items-stretch sm:items-center sm:justify-end gap-2">
              <button
                onClick={() => setShowCustom(false)}
                className="px-4 py-2.5 rounded-lg text-sm hover:bg-white/5"
                style={{ color: 'hsl(var(--text-muted))', border: '1px solid hsl(var(--border))' }}
                data-testid="credits-custom-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={submitCustom}
                disabled={!customUsd || Number(customUsd) < 1}
                className="px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40"
                style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
                data-testid="credits-custom-submit"
              >
                {user?.role === 'admin' ? 'Acreditar' : 'Continuar a Stripe'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Sprint Corrección Crítica · Historial de recargas con empty state */}
      {showHistory && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
          onClick={() => setShowHistory(false)}
          data-testid="credits-history-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:w-[520px] sm:max-w-full rounded-t-3xl sm:rounded-2xl p-5 sm:p-6 border max-h-[92dvh] overflow-y-auto"
            style={{
              background: 'hsl(var(--bg-elev))',
              borderColor: 'hsl(var(--border))',
              paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))',
            }}
          >
            <div className="sm:hidden mx-auto w-10 h-1 rounded-full mb-3"
                 style={{ background: 'hsl(var(--border))' }} />
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold mb-1"
                     style={{ color: 'hsl(var(--accent))' }}>
                  Historial de recargas
                </div>
                <div className="text-lg font-medium" style={{ color: 'hsl(var(--text))' }}>
                  Tus compras y créditos añadidos
                </div>
              </div>
              <button
                onClick={() => setShowHistory(false)}
                className="w-8 h-8 rounded-full grid place-items-center shrink-0"
                style={{ background: 'hsl(var(--bg))', color: 'hsl(var(--text-muted))', border: '1px solid hsl(var(--border))' }}
                aria-label="Cerrar"
                data-testid="credits-history-close"
              >
                <X size={14} />
              </button>
            </div>

            {loadingHistory ? (
              <div className="py-10 text-center" data-testid="credits-history-loading">
                <Loader2 className="w-6 h-6 animate-spin mx-auto"
                         style={{ color: 'hsl(var(--accent))' }} />
                <div className="text-xs mt-3" style={{ color: 'hsl(var(--text-muted))' }}>
                  Cargando historial…
                </div>
              </div>
            ) : historyItems && historyItems.length > 0 ? (
              <ul className="space-y-2" data-testid="credits-history-list">
                {historyItems.map((row) => {
                  const isStripe = String(row.reason || '').toLowerCase().includes('stripe') ||
                                   String(row.reason || '').toLowerCase().includes('checkout') ||
                                   String(row.reason || '').toLowerCase().includes('purchase');
                  const isGrant = String(row.reason || '').toLowerCase().includes('grant') ||
                                  String(row.reason || '').toLowerCase().includes('admin');
                  const isWelcome = String(row.reason || '').toLowerCase().includes('welcome');
                  const label = isStripe ? 'Compra Stripe' :
                                isWelcome ? 'Regalo de bienvenida' :
                                isGrant ? 'Acreditado por admin' :
                                (row.reason || 'Recarga');
                  const icon = isStripe ? <Lock size={14} /> :
                               isWelcome ? <Sparkles size={14} /> :
                               <Coins size={14} />;
                  return (
                    <li key={row.id}
                        className="rounded-xl p-3 flex items-center gap-3"
                        style={{ background: 'hsl(var(--bg) / 0.55)', border: '1px solid hsl(var(--border))' }}
                        data-testid={`credits-history-item-${row.id}`}>
                      <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0"
                            style={{
                              background: 'hsl(var(--accent) / 0.14)',
                              border: '1px solid hsl(var(--accent) / 0.35)',
                              color: 'hsl(var(--accent))'
                            }}>
                        {icon}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate" style={{ color: 'hsl(var(--text))' }}>
                          {label}
                        </div>
                        <div className="text-[11px]" style={{ color: 'hsl(var(--text-muted))' }}>
                          {new Date(row.created_at).toLocaleString('es-ES', {
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-semibold tabular-nums"
                             style={{ color: 'hsl(150 60% 55%)' }}>
                          +{Number(row.delta).toLocaleString('es-ES')}
                        </div>
                        <div className="text-[10px]" style={{ color: 'hsl(var(--text-muted))' }}>
                          créditos
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              /* Empty state moderno */
              <div className="py-10 text-center" data-testid="credits-history-empty">
                <div className="mx-auto w-20 h-20 rounded-full grid place-items-center mb-4"
                     style={{
                       background: 'radial-gradient(closest-side, hsl(var(--accent) / 0.15), transparent 70%)',
                       border: '1px dashed hsl(var(--accent) / 0.35)',
                     }}>
                  <History size={28} style={{ color: 'hsl(var(--accent))' }} />
                </div>
                <div className="text-base font-medium" style={{ color: 'hsl(var(--text))' }}>
                  Aún no has realizado ninguna recarga
                </div>
                <div className="text-xs mt-2 leading-relaxed max-w-xs mx-auto"
                     style={{ color: 'hsl(var(--text-muted))' }}>
                  Cuando compres créditos, aparecerán aquí junto con el detalle de cada operación.
                </div>
                <button
                  onClick={() => { setShowHistory(false); setSelected(featured); }}
                  className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold"
                  style={{
                    background: 'hsl(var(--accent))',
                    color: 'hsl(210 40% 8%)',
                    boxShadow: '0 8px 22px -8px hsl(var(--accent) / 0.7)',
                  }}
                  data-testid="credits-history-cta"
                >
                  <Plus size={12} /> {t('credits.buyCredits')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────
// NOTE: HolographicNexusCoin is imported at the top from
// `components/nx/NexusCoin` — the unified holographic coin primitive.

const PackageCard: React.FC<{ pkg: Pkg; onSelect: () => void }> = ({ pkg, onSelect }) => {
  const t = useT();
  const { locale } = useLocale();
  const localeTag = locale === 'en' ? 'en-US' : 'es-ES';
  return (
  <button
    onClick={onSelect}
    data-testid={`credits-package-${pkg.credits}`}
    className="relative text-left rounded-2xl p-4 transition hover:-translate-y-0.5"
    style={{
      background: pkg.featured
        ? 'linear-gradient(155deg, hsl(var(--accent) / 0.14), hsl(var(--accent) / 0.02))'
        : 'hsl(var(--bg-elev) / 0.55)',
      border: `1px solid ${pkg.featured ? 'hsl(var(--accent) / 0.65)' : 'hsl(var(--border))'}`,
      boxShadow: pkg.featured
        ? '0 12px 40px -14px hsl(var(--accent) / 0.5), inset 0 1px 0 hsl(0 0% 100% / 0.05)'
        : 'inset 0 1px 0 hsl(0 0% 100% / 0.03)',
      backdropFilter: 'blur(10px)',
    }}
  >
    {pkg.featured && (
      <span
        className="absolute -top-2.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold"
        style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)', boxShadow: '0 6px 18px -6px hsl(var(--accent) / 0.7)' }}
        data-testid="credits-package-featured-badge"
      >
        <Sparkles size={9} /> {t('credits.packageMostPopular')}
      </span>
    )}
    <Coins size={16} style={{ color: pkg.featured ? 'hsl(var(--accent))' : 'hsl(var(--text-muted))' }} />
    <div className="mt-2 text-3xl font-bold tabular-nums" style={{ color: pkg.featured ? 'hsl(var(--accent))' : 'hsl(var(--text))' }}>
      {pkg.credits.toLocaleString(localeTag)}
    </div>
    <div className="text-[10px] uppercase tracking-widest" style={{ color: 'hsl(var(--text-muted))' }}>{t('credits.creditsWord')}</div>
    <div className="mt-2 font-mono text-sm" style={{ color: 'hsl(var(--text))' }}>${pkg.price_usd.toFixed(2)} <span className="text-[10px]" style={{ color: 'hsl(var(--text-muted))' }}>USD</span></div>
    <div
      className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium"
      style={pkg.featured
        ? { background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }
        : { background: 'hsl(var(--bg) / 0.5)', color: 'hsl(var(--text))', border: '1px solid hsl(var(--border))' }
      }
    >
      {t('credits.packageBuy')} <ArrowRight size={11} />
    </div>
  </button>
);
};

// ─── Purchase modal (image 5) ─────────────────────────────────────
const PurchaseModal: React.FC<{ pkg: Pkg; onClose: () => void; onConfirm: () => void }> = ({ pkg, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const description = PKG_DESCRIPTIONS[pkg.id] || 'Ideal para tu próximo proyecto';
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4 overflow-y-auto"
      style={{ background: 'hsl(215 30% 3% / 0.85)', backdropFilter: 'blur(10px)' }}
      onClick={onClose}
      data-testid="purchase-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm rounded-3xl p-5 sm:p-6"
        style={{
          background: 'linear-gradient(160deg, hsl(var(--bg-elev)), hsl(215 25% 5%))',
          border: '1px solid hsl(var(--accent) / 0.55)',
          boxShadow: '0 30px 80px -20px hsl(var(--accent) / 0.4), 0 0 42px hsl(var(--accent) / 0.25)',
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full grid place-items-center"
          style={{ background: 'hsl(var(--bg) / 0.7)', color: 'hsl(var(--text-muted))', border: '1px solid hsl(var(--border))' }}
          aria-label="Cerrar" data-testid="purchase-modal-close"
        >
          <X size={14} />
        </button>

        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: 'hsl(var(--accent))' }}>
            Estás eligiendo
          </div>
          <div className="mt-3 flex justify-center">
            <HolographicNexusCoin size={100} />
          </div>
          <div className="mt-3 text-6xl font-semibold tabular-nums leading-none"
            style={{ color: 'hsl(var(--accent))', textShadow: '0 0 24px hsl(var(--accent) / 0.55)' }}
            data-testid="purchase-modal-credits">
            {pkg.credits.toLocaleString('es-ES')}
          </div>
          <div className="text-sm mt-1" style={{ color: 'hsl(var(--text))' }}>créditos</div>
          <div className="text-xs mt-2" style={{ color: 'hsl(var(--text-muted))' }}>{description}</div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-2">
          <div className="flex items-center justify-between rounded-xl p-3.5"
            style={{ background: 'hsl(var(--bg) / 0.6)', border: '1px solid hsl(var(--border))' }}>
            <div className="text-[11px] uppercase tracking-widest" style={{ color: 'hsl(var(--text-muted))' }}>Vas a pagar</div>
            <div className="text-2xl font-bold tabular-nums" style={{ color: 'hsl(var(--text))' }}>
              ${pkg.price_usd.toFixed(2)} <span className="text-xs font-normal" style={{ color: 'hsl(var(--text-muted))' }}>USD</span>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-xl p-3"
            style={{ background: 'hsl(var(--bg) / 0.4)', border: '1px solid hsl(var(--border))' }}>
            <Lock size={12} style={{ color: 'hsl(var(--accent))' }} />
            <div className="text-[11px]" style={{ color: 'hsl(var(--text-muted))' }}>
              Pago 100% seguro con <span style={{ color: 'hsl(var(--accent))', fontWeight: 500 }}>Stripe</span>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          {[
            { icon: <Zap size={13} />, title: 'Entrega inmediata', sub: 'Los créditos se activan al instante.' },
            { icon: <ShieldCheck size={13} />, title: 'Pago seguro', sub: 'Tus pagos están protegidos.' },
            { icon: <Clock size={13} />, title: 'Sin expiración', sub: 'Usa los créditos cuando quieras.' },
          ].map((f, i) => (
            <div key={i} className="text-center rounded-xl p-2.5"
              style={{ background: 'hsl(var(--bg) / 0.5)', border: '1px solid hsl(var(--border))' }}>
              <div className="inline-flex items-center justify-center mb-1" style={{ color: 'hsl(var(--accent))' }}>{f.icon}</div>
              <div className="text-[10px] font-medium" style={{ color: 'hsl(var(--text))' }}>{f.title}</div>
              <div className="text-[9px] mt-0.5" style={{ color: 'hsl(var(--text-muted))' }}>{f.sub}</div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between rounded-xl p-3"
          style={{ background: 'hsl(var(--bg) / 0.55)', border: '1px solid hsl(var(--accent) / 0.25)' }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: 'hsl(var(--text-muted))' }}>Cantidad</div>
            <div className="text-sm font-semibold" style={{ color: 'hsl(var(--text))' }}>{pkg.credits.toLocaleString('es-ES')} créditos</div>
          </div>
          <span className="text-lg" style={{ color: 'hsl(var(--accent))' }}>=</span>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest" style={{ color: 'hsl(var(--text-muted))' }}>Total a pagar</div>
            <div className="text-sm font-semibold" style={{ color: 'hsl(var(--accent))' }}>${pkg.price_usd.toFixed(2)} USD</div>
          </div>
        </div>

        <button
          onClick={async () => { setLoading(true); await onConfirm(); setLoading(false); }}
          disabled={loading}
          className="w-full mt-5 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold disabled:opacity-60"
          style={{
            background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)',
            boxShadow: '0 12px 30px -12px hsl(var(--accent) / 0.7)',
          }}
          data-testid="purchase-modal-confirm"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={14} />}
          Pagar ahora
        </button>
        <div className="text-center text-[10px] mt-3 inline-flex items-center gap-1 justify-center w-full" style={{ color: 'hsl(var(--text-muted))' }}>
          <Lock size={10} /> Procesado de forma segura con Stripe
        </div>
      </div>
    </div>
  );
};

// ─── Success modal (image 6) ─────────────────────────────────────
const SuccessModal: React.FC<{ pkg: Pkg; onClose: () => void; onContinue: () => void }> = ({ pkg, onClose, onContinue }) => (
  <div
    className="fixed inset-0 z-50 grid place-items-center p-4 overflow-y-auto"
    style={{ background: 'hsl(215 30% 3% / 0.85)', backdropFilter: 'blur(10px)' }}
    onClick={onClose}
    data-testid="success-modal"
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="relative w-full max-w-md rounded-3xl p-8 text-center"
      style={{
        background: 'linear-gradient(160deg, hsl(var(--bg-elev)), hsl(215 25% 5%))',
        border: '1px solid hsl(var(--accent) / 0.55)',
        boxShadow: '0 30px 80px -20px hsl(var(--accent) / 0.4), 0 0 42px hsl(var(--accent) / 0.25)',
      }}
    >
      {/* Big cyan check with concentric aura */}
      <div className="relative mx-auto" style={{ width: 120, height: 120 }}>
        <div className="absolute inset-0 rounded-full"
          style={{ background: 'radial-gradient(closest-side, hsl(var(--accent) / 0.32), transparent 70%)' }} />
        <div className="absolute inset-[10%] rounded-full"
          style={{ border: '1px solid hsl(var(--accent) / 0.4)', animation: 'nx-pulse 2.6s ease-in-out infinite' }} />
        <div className="absolute inset-[22%] rounded-full grid place-items-center"
          style={{
            background: 'radial-gradient(circle at 30% 30%, hsl(215 30% 10%), hsl(215 30% 4%))',
            border: '1.5px solid hsl(var(--accent))',
            boxShadow: '0 0 30px hsl(var(--accent) / 0.55)',
          }}>
          <CheckCircle2 size={44} style={{ color: 'hsl(var(--accent))', filter: 'drop-shadow(0 0 12px hsl(var(--accent) / 0.7))' }} />
        </div>
      </div>

      <h2 className="mt-5 text-2xl font-bold" style={{ color: 'hsl(var(--text))' }}>¡Pago exitoso!</h2>
      <div className="text-sm mt-1" style={{ color: 'hsl(var(--text-muted))' }}>
        Tu compra fue completada<br />con éxito.
      </div>

      <div className="mt-6 space-y-2">
        <div className="rounded-2xl p-3.5 flex items-center gap-3"
          style={{ background: 'hsl(var(--bg) / 0.55)', border: '1px solid hsl(var(--border))' }}>
          <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0"
            style={{ background: 'hsl(var(--accent) / 0.14)', border: '1px solid hsl(var(--accent) / 0.35)', color: 'hsl(var(--accent))' }}>
            <Coins size={16} />
          </span>
          <div className="text-left">
            <div className="text-sm font-medium">{pkg.credits.toLocaleString('es-ES')} créditos</div>
            <div className="text-[11px]" style={{ color: 'hsl(var(--text-muted))' }}>Se han agregado a tu cuenta.</div>
          </div>
        </div>
        <div className="rounded-2xl p-3.5 flex items-center gap-3"
          style={{ background: 'hsl(var(--bg) / 0.55)', border: '1px solid hsl(var(--border))' }}>
          <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0"
            style={{ background: 'hsl(var(--accent) / 0.14)', border: '1px solid hsl(var(--accent) / 0.35)', color: 'hsl(var(--accent))' }}>
            <ShieldCheck size={16} />
          </span>
          <div className="text-left">
            <div className="text-sm font-medium">Transacción segura</div>
            <div className="text-[11px]" style={{ color: 'hsl(var(--text-muted))' }}>Procesado con Stripe.</div>
          </div>
        </div>
      </div>

      <button
        onClick={onContinue}
        className="w-full mt-6 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold"
        style={{
          background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)',
          boxShadow: '0 12px 30px -12px hsl(var(--accent) / 0.7)',
        }}
        data-testid="success-modal-continue"
      >
        Continuar reconstruyendo <ArrowRight size={14} />
      </button>
      <div className="text-[11px] mt-3" style={{ color: 'hsl(var(--text-muted))' }}>
        Tus créditos ya están listos para usar.
      </div>
    </div>
  </div>
);
